// Costos y Claves — helpers compartidos por la página y sus modales.
import React from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

// OWNER-ONLY (mismo lock que el backend, routes/costsHub.js). Nunca bajo
// impersonación: si Andy entra "como" otro, esto no existe.
export const COSTS_HUB_USER_IDS = new Set(['eafdff80-02d2-4eef-9e5c-19f57761b85d']);
export const canUseCostsHub = (user, isImpersonating) =>
  !!user && COSTS_HUB_USER_IDS.has(user.id) && !isImpersonating;

export const PAYERS = ['andy', 'ayudatecno', 'client'];
// Color de cada pagador en el gráfico: se asigna por ENTIDAD, nunca por rango.
export const PAYER_COLOR = { andy: 'var(--c-viz-1)', ayudatecno: 'var(--c-viz-2)', client: 'var(--c-viz-3)' };

// De dónde sale cada número. El punto de la pantalla es que un estimado no se
// vea igual que un dato real del proveedor.
export const SOURCE_META = {
  api:      { color: 'var(--c-resolved)' },
  manual:   { color: 'var(--c-brand)' },
  estimate: { color: 'var(--c-pending)' },
  internal: { color: 'var(--c-text-muted)' },
};

export const usd = (n, digits = 2) => (n == null || !Number.isFinite(Number(n)) ? '—'
  : Number(n).toLocaleString('es-AR', { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits }));
export const ars = (n) => (n == null || !Number.isFinite(Number(n)) ? '—'
  : Number(n).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }));
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
export const fmtPeriod = (p, lang = 'es') => {
  if (!p) return '—';
  const [y, m] = p.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString(lang === 'en' ? 'en-US' : 'es-AR', { month: 'short', year: '2-digit', timeZone: 'UTC' });
};
export const keyMask = (k) => `${k.key_prefix || ''}…${k.key_last4 || ''}`;

export function SourceChip({ source, partial, t }) {
  if (!source) return null;
  const meta = SOURCE_META[source] || SOURCE_META.internal;
  return (
    <span className="badge" title={t(`costsHub.sourceHelp.${partial ? 'partial' : source}`)}
      style={{ background: 'var(--c-surface-2)', color: 'var(--c-text-muted)', display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
      {t(`costsHub.source.${partial ? 'partial' : source}`)}
    </span>
  );
}

export function PayerBadge({ payer, company, t }) {
  return (
    <span className="badge" style={{ background: 'var(--c-surface-2)', color: 'var(--c-text)', display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: PAYER_COLOR[payer] || 'var(--c-border)', flexShrink: 0 }} />
      {payer === 'client' && company ? company : t(`costsHub.payer.${payer}`)}
    </span>
  );
}

// Esqueleto estándar de modal del helpdesk: portal a document.body, centrado a la
// ventana, cierre con onMouseDown en el fondo (un arrastre que empieza adentro
// nunca lo cierra).
export function Modal({ title, onClose, children, footer, maxWidth = 760 }) {
  const node = (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)', padding: 16 }}>
      <div onMouseDown={(e) => e.stopPropagation()} className="card"
        style={{ margin: 0, maxWidth, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 0 }}>
        <div className="card-header" style={{ position: 'sticky', top: 0, background: 'var(--c-surface)', zIndex: 1, padding: '14px 18px', borderBottom: '1px solid var(--c-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div className="card-title" style={{ minWidth: 0 }}>{title}</div>
          <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-label="close"><X size={16} /></button>
        </div>
        <div style={{ padding: 18 }}>{children}</div>
        {footer && (
          <div style={{ position: 'sticky', bottom: 0, background: 'var(--c-surface)', padding: '12px 18px', borderTop: '1px solid var(--c-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
  return ReactDOM.createPortal(node, document.body);
}
